// basic observer configuration for the print target
const NODE_OBSERVER_CONFIG = { attributes: false, childList: true, subtree: true };

type CreateNodeObserverArgs = {
  /** The node to observe */
  node: Node | HTMLElement;
  /** The callback to execute when the node has successfully loaded content in it's childlist */
  onObservedChange: () => void;
  /** The window the observation is attached to, used to add an onclose listener to the window */
  window?: Window;
};

type createNodeObserver = (args: CreateNodeObserverArgs) => {
  observer: MutationObserver;
  disconnect: () => void;
};

/**
 * Creates a mutation observer for the given node
 * This is used to detect when the given node has successfully loaded the content.
 *
 * Returns the observer instance so it can be disconnected when the node is no longer needed.
 */
const createNodeObserver: createNodeObserver = ({ node, onObservedChange, window }) => {
  let windowCloseHandler: () => void;
  // Callback function to execute when mutations are observed
  const callback: MutationCallback = (mutationList) => {
    for (const mutation of mutationList) {
      if (mutation.type === 'childList') {
        if (mutation.addedNodes.length > 0) {
          onObservedChange();
          return; // exit the callback function
        }
      }
    }
  };

  // Create an observer instance linked to the callback function
  const observer = new MutationObserver(callback);

  // Start observing the target node for configured mutations
  observer.observe(node, NODE_OBSERVER_CONFIG);

  const disconnect = () => {
    observer.disconnect();
    if (window) {
      window.removeEventListener('close', windowCloseHandler);
    }
  };

  if (window) {
    windowCloseHandler = () => {
      disconnect();
    };
    window.addEventListener('close', windowCloseHandler);
  }

  return { observer, disconnect };
};

export { createNodeObserver };
